import { useCallback, useEffect, useState } from 'react';

import {
  fetchKnowledgeNoteCard,
  updateKnowledgeDocumentSummary,
  updateKnowledgeNote,
  type KnowledgeCardNotePayloadDTO,
} from '@/api/knowledge/api';

export type { KnowledgeCardNotePayloadDTO } from '@/api/knowledge/api';

export interface UseAssetContextResult {
  card: KnowledgeCardNotePayloadDTO | null;
  isLoading: boolean;
  isSaving: boolean;
  loadError: string | null;
  saveError: string | null;
  editTitle: string;
  editContent: string;
  tags: string[];
  setEditTitle: (value: string) => void;
  setEditContent: (value: string) => void;
  setTags: (value: string[]) => void;
  /**
   * Persists edits then fires `onLink`.
   * `getMarkdown` is called at save time to get the live value from the Milkdown editor ref.
   */
  handleSaveAndLink: (getMarkdown: () => string) => Promise<void>;
}

/**
 * Manages fetch, edit, and save state for the AssetContextModal.
 * The modal's editor ref stays in the component; `getMarkdown` is passed at save time.
 */
export function useAssetContext(
  open: boolean,
  cardId: string | null,
  accessToken: string | null,
  clientId: string,
  projectId: string,
  onLink: (cardId: string) => void,
  initialCard?: KnowledgeCardNotePayloadDTO,
): UseAssetContextResult {
  const [card, setCard] = useState<KnowledgeCardNotePayloadDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  // Fetch card when modal opens (or hydrate from initialCard seed)
  useEffect(() => {
    if (!open) {
      return;
    }

    if (initialCard) {
      const isNote = initialCard.source_type === 'note';
      setCard(initialCard);
      setEditTitle(initialCard.title);
      setEditContent(
        isNote ? (initialCard.markdown_content ?? '') : (initialCard.summary_text ?? ''),
      );
      setTags(initialCard.tags ?? []);
      return;
    }

    if (!cardId || !accessToken) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    setSaveError(null);
    fetchKnowledgeNoteCard(accessToken, clientId, cardId, projectId)
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result.found && result.card) {
          const loaded = result.card;
          setCard(loaded);
          setEditTitle(loaded.title);
          const isNote = loaded.source_type === 'note';
          setEditContent(isNote ? (loaded.markdown_content ?? '') : (loaded.summary_text ?? ''));
          setTags(loaded.tags ?? []);
        } else {
          setCard(null);
          setLoadError('Card context not available yet — you can still link this asset.');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('Failed to load card details.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, cardId, accessToken, clientId, projectId, initialCard]);

  // Reset all state when the modal closes
  useEffect(() => {
    if (!open) {
      setCard(null);
      setEditTitle('');
      setEditContent('');
      setTags([]);
      setLoadError(null);
      setSaveError(null);
    }
  }, [open]);

  const handleSaveAndLink = useCallback(
    async (getMarkdown: () => string): Promise<void> => {
      if (!cardId) {
        return;
      }
      setSaveError(null);
      setIsSaving(true);
      try {
        if (card && accessToken) {
          const markdown = getMarkdown();
          if (card.source_type === 'note') {
            await updateKnowledgeNote(accessToken, clientId, cardId, {
              title: editTitle,
              markdown_content: markdown,
              tags,
            });
          } else {
            await updateKnowledgeDocumentSummary(accessToken, clientId, cardId, {
              title: editTitle,
              summary_text: markdown,
            });
          }
        }
        onLink(cardId);
      } catch {
        setSaveError('Failed to save changes. Please try again.');
      } finally {
        setIsSaving(false);
      }
    },
    [card, accessToken, cardId, clientId, editTitle, tags, onLink],
  );

  return {
    card,
    isLoading,
    isSaving,
    loadError,
    saveError,
    editTitle,
    editContent,
    tags,
    setEditTitle,
    setEditContent,
    setTags,
    handleSaveAndLink,
  };
}
